/**
 * Login and registration page for DiceCapture
 *
 * Full-screen authentication page with toggle between sign-in and
 * create-account modes. Includes forgot password flow (inline state
 * machine) and guest mode. No Layout wrapper — renders independently
 * with its own branding header and footer.
 */

import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AppWelcomeDrawer } from '@/components/AppWelcomeDrawer';
import { ForgotPasswordFlow } from '@/components/ForgotPasswordFlow';
import { VARIANT_FILES } from '@/components/FrogMascot';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ACTIONS } from '@/constants/terminology';
import { extractZodFieldErrors } from '@/lib/utils';
import { loginFormSchema, registerFormSchema } from '@/schemas/auth';
import { useAuthStore } from '@/stores/authStore';

/** Extract the redirect path from router location state */
function getRedirectPath(state: unknown): string {
  return (state as { from?: { pathname: string } })?.from?.pathname || '/';
}

/**
 * Login/registration page with form validation, forgot password flow,
 * guest mode, and Cognito integration.
 *
 * Validates input using Zod schemas from `@/schemas/auth`.
 * On success, navigates to the originally intended route or `/`.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    register,
    isAuthenticated,
    isGuest,
    isLoading,
    error: storeError,
    clearError,
    clearGuest,
    continueAsGuest,
    forgotPassword,
    confirmForgotPassword
  } = useAuthStore();

  /** Router state may specify initial mode (e.g. guest → sign up) */
  const initialMode = (location.state as { mode?: string })?.mode === 'register'
    ? 'register' as const
    : 'login' as const;

  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Preload all frog images so page splashes are instant after login
  useEffect(() => {
    Object.values(VARIANT_FILES).forEach((file) => {
      const img = new Image();
      img.src = `/images/frog/${file}`;
    });
  }, []);

  const [showForgotFlow, setShowForgotFlow] = useState(false);

  // Clear guest status when arriving from a "Sign up" link so the page renders
  useEffect(() => {
    if (isGuest && initialMode === 'register') {
      clearGuest();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect authenticated users (and guests) away from the login page
  // Allow guests through when they arrived via a sign-up link (initialMode === 'register')
  if (isAuthenticated || (isGuest && initialMode !== 'register')) {
    return <Navigate to={getRedirectPath(location.state)} replace />;
  }

  const isLogin = mode === 'login';

  /**
   * Toggle between login and register modes.
   * Clears validation errors and sensitive fields on switch.
   */
  function handleModeToggle() {
    setMode(isLogin ? 'register' : 'login');
    setErrors({});
    setPassword('');
    setConfirmPassword('');
    setShowForgotFlow(false);
    clearError();
  }

  /**
   * Validate and submit the authentication form.
   *
   * Uses `loginFormSchema` or `registerFormSchema` for validation.
   * On success, calls the appropriate auth store action and navigates
   * to the route the user was trying to reach, or `/` by default.
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isLogin) {
      const result = loginFormSchema.safeParse({ email, password });
      if (!result.success) {
        setErrors(extractZodFieldErrors(result.error));
        return;
      }

      setErrors({});
      try {
        await login(result.data.email, result.data.password);
        navigate(getRedirectPath(location.state), { replace: true });
      } catch {
        // Error is set in the store by login()
      }
    } else {
      const result = registerFormSchema.safeParse({
        username,
        email,
        password,
        confirmPassword
      });
      if (!result.success) {
        setErrors(extractZodFieldErrors(result.error));
        return;
      }

      setErrors({});
      try {
        await register(result.data.username, result.data.email, result.data.password);
        navigate(getRedirectPath(location.state), { replace: true });
      } catch {
        // Error is set in the store by register()
      }
    }
  }

  /** Handle "Continue as Guest" */
  function handleGuestMode() {
    continueAsGuest();
    navigate(getRedirectPath(location.state), { replace: true });
  }

  return (
    <div className="flex h-svh flex-col items-center justify-center bg-background overflow-hidden p-4">
      <div className="w-full max-w-sm space-y-4">
        <PageHeader
          variant={showForgotFlow ? 'lost' : isLogin ? 'logo' : 'welcome'}
          title={showForgotFlow ? 'Reset Password' : isLogin ? ACTIONS.SIGN_IN : ACTIONS.CREATE_ACCOUNT}
        />

        {/* Auth Card */}
        <Card variant="elevated">
          <CardContent>
            {showForgotFlow ? (
              <ForgotPasswordFlow
                forgotPassword={forgotPassword}
                confirmForgotPassword={confirmForgotPassword}
                onBackToLogin={() => {
                  setShowForgotFlow(false);
                  clearError();
                }}
              />
            ) : (
              <>
                {/* Store error banner */}
                {storeError && (
                  <p className="mb-4 text-sm text-destructive bg-destructive/10 rounded-md p-2">
                    {storeError}
                  </p>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  {/* Username (register only) */}
                  {!isLogin && (
                    <div className="space-y-1.5">
                      <label htmlFor="username" className="text-sm font-medium">
                        Username
                      </label>
                      <Input
                        id="username"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          clearError();
                        }}
                        maxLength={50}
                        autoFocus={!isLogin}
                      />
                      {errors.username && (
                        <p className="text-xs text-destructive">{errors.username}</p>
                      )}
                    </div>
                  )}

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="text-sm font-medium">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearError();
                      }}
                      autoFocus={isLogin}
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div className="space-y-1.5">
                    <label htmlFor="password" className="text-sm font-medium">
                      Password
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          clearError();
                        }}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-destructive">{errors.password}</p>
                    )}
                    {/* Forgot password link (login only) */}
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotFlow(true);
                          clearError();
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>

                  {/* Confirm Password (register only) */}
                  {!isLogin && (
                    <div className="space-y-1.5">
                      <label
                        htmlFor="confirm-password"
                        className="text-sm font-medium"
                      >
                        Confirm Password
                      </label>
                      <Input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      {errors.confirmPassword && (
                        <p className="text-xs text-destructive">
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Submit */}
                  <Button
                    type="submit"
                    variant="accent"
                    className="w-full"
                    enablePressEffect
                    disabled={isLoading}
                  >
                    {isLoading
                      ? (isLogin ? 'Signing in...' : 'Creating account...')
                      : (isLogin ? ACTIONS.SIGN_IN : ACTIONS.CREATE_ACCOUNT)}
                  </Button>
                </form>

                {/* Mode Toggle */}
                <p className="mt-4 text-center text-sm text-muted-foreground">
                  {isLogin
                    ? "Don't have an account? "
                    : 'Already have an account? '}
                  <button
                    type="button"
                    onClick={handleModeToggle}
                    className="text-primary hover:underline font-medium"
                  >
                    {isLogin ? 'Create one' : 'Sign in'}
                  </button>
                </p>

                {/* Guest Mode */}
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  <button
                    type="button"
                    onClick={handleGuestMode}
                    className="text-primary hover:underline font-medium"
                  >
                    Continue as Guest
                  </button>
                </p>
              </>
            )}
          </CardContent>
        </Card>

      </div>
      <AppWelcomeDrawer />
    </div>
  );
}
